import React from 'react';
import SimpleVWAPChart from '../components/SimpleVWAPChart';
import DseUniverseTable from '../components/DseUniverseTable';
import { useMarketData, useTrading, useEngine, useConnection } from '../context/MarketContext';
import { useMicropriceData } from '../store/hooks';
import type { EngineRegime, RegimeState } from '../types';
import { Card, Panel } from '../components/ui';
import { colors, severityColor } from '../styles/tokens';

// ─── helpers ─────────────────────────────────────────────────────────────────

const REGIME_COLORS: Record<EngineRegime, string> = {
  ACTIVE:       colors.accent.green,
  accumulation: colors.accent.green,
  QUIET:        colors.accent.blue,
  neutral:      colors.text.secondary,
  DORMANT:      colors.text.muted,
  VOLATILE:     colors.accent.red,
  distribution: colors.accent.yellow,
};

const REGIME_DESC: Record<EngineRegime, string> = {
  ACTIVE:       'High activity · tight spreads · momentum building',
  accumulation: 'Steady buying pressure building',
  QUIET:        'Low volatility · wider spreads · range-bound',
  neutral:      'Mixed signals · no clear direction',
  DORMANT:      'Minimal activity · stale book · low liquidity',
  VOLATILE:     'Elevated risk · sharp moves · spread widening',
  distribution: 'Selling pressure · positions unwinding',
};

const SEVERITY_COLORS = severityColor;

function relTime(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

/**
 * Money, in full. P&L is a figure an operator reconciles against a statement, so an abbreviated
 * '+1.1M' is the wrong trade-off: it hides the digits that matter and cannot be checked. Volume
 * and other scale-reading figures keep fmtK.
 */
function fmtMoney(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return Math.round(v).toLocaleString('en-US');
}

function fmtK(v: number, dec = 1): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(dec)}M`;
  if (abs >= 1_000)     return `${(v / 1_000).toFixed(dec)}K`;
  return v.toFixed(0);
}

// ─── KPI cell ─────────────────────────────────────────────────────────────────

/**
 * One AT A GLANCE cell.
 *
 * Typography follows dse-terminal exactly, because the hierarchy is what makes the strip read
 * as an instrument: 9px/700 dim label -> 16px/800 MONO tabular ink value -> 10px dim sub. The
 * value is the only mono-faced and only ink-coloured element, which is what makes it pop.
 * Separators are a border on the cell, not separate elements.
 */
function KpiCell({ label, children, sub, subColor, hardLeft }: {
  label: string;
  children: React.ReactNode;
  sub?: React.ReactNode;
  subColor?: string;
  /** Starts a new group (the desk-side half) with a heavier rule. */
  hardLeft?: boolean;
}) {
  return (
    <div style={{
      flex: '1 1 0', minWidth: 96, display: 'flex', flexDirection: 'column',
      justifyContent: 'center', gap: 3, padding: '4px 14px',
      borderLeft: hardLeft ? `2px solid ${colors.border.default}` : `1px solid ${colors.border.subtle}`,
    }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.07em' }}>{label}</span>
      {/* Display face, not mono: the mono figure read as a different product from the rest of
          the terminal. tabular-nums still keeps the digits column-aligned. */}
      <div style={{ fontSize: 16, fontWeight: 800, color: colors.text.primary, fontVariantNumeric: 'tabular-nums' }}>{children}</div>
      {sub && <span style={{ fontSize: 10, color: subColor ?? colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>{sub}</span>}
    </div>
  );
}

/** Retained as a no-op so existing call sites keep compiling; separators are borders now. */
const Divider = () => null;

// ─── main ─────────────────────────────────────────────────────────────────────

export interface DashboardProps {
  instruments: { label: string; secId: string }[];
  addInstrument: (label: string, secId: string) => void;
  removeInstrument: (secId: string) => void;
}

export default function Dashboard({ instruments, addInstrument, removeInstrument }: DashboardProps) {
  const { selectedSymbol, labelForSecId, currentPrice, stats, orderBook, recentTrades, analytics, INSTRUMENTS } = useMarketData();
  const { portfolio, risk, positions } = useTrading();
  const { performanceSnapshot, regimeBySymbol, activityFeed } = useEngine();
  const { darHealth } = useConnection();
  const mpData = useMicropriceData(selectedSymbol);

  // KPI derivations
  const bestBid = orderBook.bids[0]?.price ?? null;
  const bestAsk = orderBook.asks[0]?.price ?? null;
  const spread  = bestBid != null && bestAsk != null ? bestAsk - bestBid : null;
  const mid     = bestBid != null && bestAsk != null ? (bestAsk + bestBid) / 2 : null;
  const spreadBps = spread != null && mid && mid > 0 ? Math.round((spread / mid) * 10_000) : null;

  const lastPrice = stats.last ?? (currentPrice > 0 ? currentPrice : null);
  const priceDelta    = lastPrice != null && stats.prevClose ? lastPrice - stats.prevClose : null;
  const priceDeltaPct = priceDelta != null && stats.prevClose ? (priceDelta / stats.prevClose) * 100 : null;
  const vwapDistBps   = stats.vwap && stats.vwap > 0 && currentPrice > 0
    ? Math.round(((currentPrice - stats.vwap) / stats.vwap) * 10_000) : null;

  const rangePos = stats.high != null && stats.low != null && stats.high > stats.low && lastPrice != null
    ? Math.max(0, Math.min(100, ((lastPrice - stats.low) / (stats.high - stats.low)) * 100))
    : null;

  const fallbackUnrealized = positions.reduce((s, p) => s + Number(p.unrealizedPnL ?? 0), 0);
  const fallbackRealized = positions.reduce((s, p) => s + Number(p.realizedPnL ?? 0), 0);
  const hasPortfolioTotals = portfolio.totalUnrealizedPnL !== 0 || portfolio.totalRealizedPnL !== 0 || portfolio.totalPnL !== 0;
  const pnlUnrealized = hasPortfolioTotals ? portfolio.totalUnrealizedPnL : fallbackUnrealized;
  const pnlRealized = hasPortfolioTotals ? portfolio.totalRealizedPnL : fallbackRealized;
  const netPnl = pnlUnrealized + pnlRealized;
  const latestTrade = [...recentTrades]
    .filter(t => !t.symbol || t.symbol.toUpperCase() === selectedSymbol.toUpperCase())
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())[0];

  // Regime
  const regimeState = regimeBySymbol.get(selectedSymbol) ?? regimeBySymbol.get(labelForSecId(selectedSymbol));
  const regime      = regimeState?.regime;
  const regimeColor = regime ? REGIME_COLORS[regime] : colors.text.muted;

  // Book pressure
  let buyPct = 50;
  if (analytics?.bookPressure) {
    const total = analytics.bookPressure.buyPressure + analytics.bookPressure.sellPressure;
    if (total > 0) buyPct = Math.round((analytics.bookPressure.buyPressure / total) * 100);
  } else {
    const bidVol = orderBook.bids.slice(0, 5).reduce((a, b) => a + b.size, 0);
    const askVol = orderBook.asks.slice(0, 5).reduce((a, b) => a + b.size, 0);
    const total  = bidVol + askVol;
    if (total > 0) buyPct = Math.round((bidVol / total) * 100);
  }
  const sellPct = 100 - buyPct;

  // Microprice
  const latestMp  = mpData.length > 0 ? mpData[mpData.length - 1] : null;
  const microprice = latestMp?.microprice ?? analytics?.microprice?.microprice ?? null;
  const midForMp  = latestMp?.bidPrice != null && latestMp?.askPrice != null
    ? (latestMp.bidPrice + latestMp.askPrice) / 2 : mid;
  const mpDelta   = microprice != null && midForMp ? microprice - midForMp : null;

  const feedItems = activityFeed.slice(0, 20);

  return (
    <>
      {/* ── Zone 1: the listed universe. dse-terminal opens Overview with this, and it is the
             right call: it frames every figure below with how much of the market we cover. ── */}
      <div style={{ marginBottom: 16 }}>
        <DseUniverseTable
          instruments={instruments}
          allInstruments={INSTRUMENTS}
          onAdd={addInstrument}
          onRemove={removeInstrument}
        />
      </div>

      {/* ── Zone 2: AT A GLANCE ──────────────────────────────────────────── */}
      <div style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: colors.text.primary, display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8,
      }}>
        <span style={{ width: 5, height: 11, borderRadius: 1, background: colors.chrome.base, boxShadow: `0 0 6px ${colors.chrome.base}` }} />
        {labelForSecId(selectedSymbol)} — at a glance
      </div>
      <Card padding={0} style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', overflow: 'hidden' }}>
        <KpiCell
          label="LAST"
          sub={priceDeltaPct != null
            ? `${priceDeltaPct >= 0 ? '+' : ''}${priceDeltaPct.toFixed(2)}% today`
            : 'vs prev close'}
          subColor={priceDeltaPct != null ? (priceDeltaPct >= 0 ? colors.accent.green : colors.accent.red) : undefined}
        >
          {lastPrice != null ? lastPrice.toFixed(2) : '—'}
          {priceDelta != null && (
            <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 6, color: priceDelta >= 0 ? colors.accent.green : colors.accent.red }}>
              {priceDelta >= 0 ? '+' : ''}{priceDelta.toFixed(2)}
            </span>
          )}
        </KpiCell>
        <Divider />

        <KpiCell
          label="vs VWAP"
          sub={stats.vwap ? `VWAP ${stats.vwap.toFixed(2)}` : undefined}
          subColor={vwapDistBps != null ? (vwapDistBps >= 0 ? colors.accent.green : colors.accent.red) : undefined}
        >
          <span style={{ color: vwapDistBps != null ? (vwapDistBps >= 0 ? colors.accent.green : colors.accent.red) : colors.text.primary }}>
            {vwapDistBps != null ? `${vwapDistBps >= 0 ? '+' : ''}${vwapDistBps} bps` : '—'}
          </span>
        </KpiCell>
        <Divider />

        <KpiCell
          label="SPREAD"
          sub={spreadBps != null ? `${spreadBps} bps` : undefined}
        >
          {spread != null ? spread.toFixed(2) : '—'}
        </KpiCell>
        <Divider />

        <KpiCell
          label="VOLUME"
          sub="session"
        >
          {stats.volume != null ? fmtK(stats.volume, 2) : '—'}
        </KpiCell>
        <Divider />

        {/* Range cell — custom layout */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 14px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.07em', marginBottom: 5 }}>RANGE</span>
          {rangePos != null ? (
            <>
              <div style={{ position: 'relative', height: 4, backgroundColor: colors.border.default, borderRadius: 2, marginBottom: 4 }}>
                <div style={{
                  position: 'absolute',
                  left: `${rangePos}%`,
                  transform: 'translateX(-50%)',
                  width: 8, height: 8, borderRadius: '50%',
                  backgroundColor: colors.text.primary,
                  top: -2,
                }} />
              </div>
              <span style={{ fontSize: 10, color: colors.text.muted, fontVariantNumeric: 'tabular-nums' }}>
                Lo {stats.low!.toFixed(0)}  ·  Hi {stats.high!.toFixed(0)}
              </span>
            </>
          ) : (
            <span style={{ fontSize: 14, fontWeight: 700, color: colors.text.muted }}>—</span>
          )}
        </div>
        <Divider />

        <KpiCell
          hardLeft
          label="NOTIONAL"
          sub="gross exposure"
          subColor="#4da3ff"
        >
          <span style={{ color: colors.accent.blue }}>
            {risk.grossNotional > 0 ? `${(risk.grossNotional / 1_000_000).toFixed(2)}M` : '—'}
          </span>
        </KpiCell>
        <Divider />

        {/* FIX / Dar health indicator */}
        <KpiCell
          label="FIX"
          sub={darHealth.lastHeartbeatAt
            ? `hb ${new Date(darHealth.lastHeartbeatAt).toLocaleTimeString()}`
            : 'no heartbeat'}
          subColor={darHealth.fixSessionStatus === 'CONNECTED' ? colors.accent.green : colors.text.muted}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              backgroundColor:
                darHealth.fixSessionStatus === 'CONNECTED'    ? colors.accent.green :
                darHealth.fixSessionStatus === 'RECONNECTING' ? colors.accent.yellow :
                darHealth.fixSessionStatus === 'DISCONNECTED' ? colors.accent.red : colors.border.default,
            }} />
            <span style={{ fontSize: 12, color: colors.text.primary }}>
              {darHealth.fixSessionStatus === 'UNKNOWN' ? '—' : darHealth.fixSessionStatus}
            </span>
          </span>
        </KpiCell>
      </Card>

      {/* ── Zone 3: chart, with the two compact readouts stacked beside it ──────
             The chart carries the height; Market Pulse and P&L share it in a column so the
             row squares off instead of leaving a ragged edge under the pulse panel. Both
             readouts are dense by design -- they answer "what is the book doing" and "where
             am I" at a glance, not in detail. */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12, alignItems: 'stretch' }}>

        {/* Chart */}
        <div style={{
          backgroundColor: colors.bg.secondary,
          borderRadius: 8,
          border: `1px solid ${colors.border.default}`,
          borderTop: `3px solid ${regimeColor}`,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: colors.text.primary }}>
              {labelForSecId(selectedSymbol)} · VWAP
            </span>
            {regime && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                color: regimeColor,
                backgroundColor: `${regimeColor}18`,
                border: `1px solid ${regimeColor}30`,
                padding: '2px 8px', borderRadius: 4,
              }}>
                ● {regime}
              </span>
            )}
          </div>
          <div style={{ padding: '0 8px 12px' }}>
            <SimpleVWAPChart symbol={selectedSymbol} height={330} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: 12, minHeight: 0 }}>
        {/* Market Pulse */}
        <Panel title="Market pulse" badge={labelForSecId(selectedSymbol)} style={{ minHeight: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>

          {/* A: Regime */}
          <div style={{
            padding: '12px 14px', borderRadius: 6,
            backgroundColor: `${regimeColor}10`,
            border: `1px solid ${regimeColor}25`,
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: regimeColor, marginBottom: 4, letterSpacing: '-0.01em' }}>
              {regime ?? 'NO DATA'}
            </div>
            <div style={{ fontSize: 11, color: colors.text.secondary, lineHeight: 1.5 }}>
              {regime ? REGIME_DESC[regime] : 'Waiting for regime data…'}
            </div>
            {regimeState?.confidence != null && (
              <div style={{ fontSize: 10, color: colors.text.muted, marginTop: 5 }}>
                Confidence {Math.round(regimeState.confidence * 100)}%
              </div>
            )}
          </div>

          {/* B: Book Pressure */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: colors.accent.green, fontWeight: 600 }}>BUY {buyPct}%</span>
              <span style={{ fontSize: 10, color: colors.text.muted, letterSpacing: '0.05em' }}>BOOK PRESSURE</span>
              <span style={{ fontSize: 11, color: colors.accent.red, fontWeight: 600 }}>SELL {sellPct}%</span>
            </div>
            <div style={{ height: 6, backgroundColor: colors.border.default, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${buyPct}%`,
                background: 'linear-gradient(90deg, #36c98e, #34d399)',
                borderRadius: 3, transition: 'width 0.4s ease',
              }} />
            </div>
          </div>

          {/* C: Last Trade */}
          <div style={{ borderTop: `1px solid ${colors.border.default}`, paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.07em', marginBottom: 8 }}>LAST TRADE</div>
            {latestTrade ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 3,
                    backgroundColor: latestTrade.side === 'buy' ? 'rgba(54,201,142,0.15)' : 'rgba(255,92,92,0.15)',
                    color: latestTrade.side === 'buy' ? colors.accent.green : colors.accent.red,
                  }}>
                    {latestTrade.side.toUpperCase()}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: colors.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                    {latestTrade.price.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 11, color: colors.text.muted }}>× {latestTrade.size.toLocaleString()}</span>
                </div>
                <span style={{ fontSize: 10, color: colors.text.muted }}>{relTime(latestTrade.time)}</span>
              </div>
            ) : (
              <span style={{ fontSize: 12, color: colors.text.muted }}>No trades yet</span>
            )}
          </div>

          {/* MICROPRICE moved out. Pulse is three blocks -- regime, book pressure, last
              trade. The full OFI / VPIN / microprice stack lives on Analysis. */}
          </div>
        </Panel>
        {/* P&L + Performance */}
        <Panel title="P&L" style={{ minHeight: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* Large net P&L number */}
          <div style={{ marginBottom: 14 }}>
            <div style={{
              fontSize: 36, fontWeight: 800, lineHeight: 1,
              color: netPnl !== 0 ? (netPnl >= 0 ? colors.accent.green : colors.accent.red) : colors.text.muted,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {netPnl !== 0 ? `${netPnl >= 0 ? '+' : ''}${fmtMoney(netPnl)}` : '—'}
            </div>
            <div style={{ fontSize: 11, color: colors.text.muted, marginTop: 4 }}>net P&L today</div>
          </div>

          {/* Breakdown row */}
          <div style={{ display: 'flex', gap: 20, paddingBottom: 14, marginBottom: 14, borderBottom: `1px solid ${colors.border.default}` }}>
              {[
              { label: 'Unrealized', value: pnlUnrealized },
              { label: 'Realized',   value: pnlRealized },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{ fontSize: 10, color: colors.text.muted, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: value !== 0 ? (value >= 0 ? colors.accent.green : colors.accent.red) : colors.text.muted }}>
                  {value !== 0 ? `${value >= 0 ? '+' : ''}${fmtMoney(value)}` : '—'}
                </div>
              </div>
            ))}
            <div>
              <div style={{ fontSize: 10, color: colors.text.muted, marginBottom: 2 }}>Notional</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.accent.blue, fontVariantNumeric: 'tabular-nums' }}>
                {risk.grossNotional > 0 ? `${(risk.grossNotional / 1_000_000).toFixed(2)}M` : '—'}
              </div>
            </div>
          </div>

          {/* PERFORMANCE moved out. dse-terminal's P&L hero is five figures: the signed
              net, a sub line, then unrealized / realized / notional. Session statistics
              belong on Post-Trade, not in a glance tile. */}
          </div>
        </Panel>
        </div>
      </div>

      {/* ── Zone 4: Activity feed ───────────────────────────────────────────── */}
      <div>
        {/* Activity Feed */}
        <Panel
          title="Activity feed"
          badge={feedItems.length > 0 ? `${feedItems.length} events` : undefined}
          flush
          style={{ height: 380 }}
        >
          <div style={{ height: '100%', overflowY: 'auto', overscrollBehavior: 'contain' }}>
            {feedItems.length === 0 ? (
              <div style={{ padding: '28px 16px', textAlign: 'center', color: colors.border.default, fontSize: 12 }}>
                Waiting for market events…
              </div>
            ) : feedItems.map(ev => {
              const borderColor = SEVERITY_COLORS[ev.severity] ?? colors.text.muted;
              return (
                <div key={ev.id} style={{
                  padding: '9px 14px',
                  borderBottom: '1px solid #1a2840',
                  borderLeft: `3px solid ${borderColor}`,
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ fontSize: 12, color: colors.text.primary, lineHeight: 1.4, flex: 1 }}>{ev.title}</span>
                    <span style={{ fontSize: 10, color: colors.text.muted, flexShrink: 0 }}>{relTime(ev.timestamp)}</span>
                  </div>
                  {ev.detail && (
                    <span style={{ fontSize: 11, color: colors.text.muted }}>{ev.detail}</span>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>


    </>
  );
}
